/**
 * Niet in het origineel: de omlijsting van de schermen rond het inloggen van de spelleider
 * (inloggen, wachtwoord vergeten, nieuw wachtwoord, uitnodiging). Dezelfde opbouw als het
 * welkomstscherm van de spelers (`Login/UserLogin.tsx`), maar rustiger: links in
 * nachtblauw wat een spelleider doet, rechts het formulier.
 */
import type { ReactNode } from 'react';
import { Alert, Box, Flex, Heading, Image, Link, Skeleton, Text } from '@chakra-ui/react';
import { Dices, LayoutGrid, UserPlus } from 'lucide-react';
import { Link as RouterLink } from 'react-router';
import { useAchtergrondkleur } from '@/components/hooks';

/** Het blauw van het logo, zoals op het welkomstscherm van de spelers. */
const NACHTBLAUW = '#0b2f55';

/** Het formulier rechts, met links de uitleg. Op een smal scherm eerst het formulier. */
export function Inlogkader({ children }: { children: ReactNode }) {
  useAchtergrondkleur('white');
  return (
    <Flex h="full" overflowY="auto" direction={{ base: 'column', lg: 'row' }}>
      <Flex
        as="main"
        flex={{ base: 'none', lg: '1' }}
        minW="0"
        bg="white"
        alignItems="center"
        justifyContent="center"
        px={{ base: '6', md: '10' }}
        py={{ base: '10', lg: '12' }}
        boxShadow={{ lg: '-12px 0 40px rgba(11, 47, 85, 0.12)' }}
      >
        <Box w="full" maxW="sm">
          {children}
        </Box>
      </Flex>
      <Uitleg />
    </Flex>
  );
}

/** Links (onder het formulier op een smal scherm): wat je als spelleider doet. */
function Uitleg() {
  return (
    <Flex
      as="section"
      aria-label="Over de spelleider"
      order={{ lg: -1 }}
      // Onder het formulier vult het blauw de rest van een smal scherm.
      flex={{ base: '1 0 auto', lg: '1.1' }}
      minW="0"
      direction="column"
      justifyContent="center"
      px={{ base: '6', md: '12', xl: '20' }}
      py={{ base: '10', lg: '12' }}
      bgGradient="to-br"
      gradientFrom={NACHTBLAUW}
      gradientTo="#103f70"
      color="white"
    >
      <Box maxW="lg">
        <Text
          textStyle="sm"
          fontWeight="bold"
          textTransform="uppercase"
          letterSpacing="widest"
          color="#9cc3f5"
        >
          Spelleider
        </Text>
        <Heading
          as="h2"
          fontSize={{ base: '3xl', md: '4xl', xl: '5xl' }}
          lineHeight="1.05"
          fontWeight="bold"
          color="white"
          mt="3"
          textWrap="balance"
        >
          Begeleid het spel
        </Heading>
        <Text textStyle={{ base: 'md', md: 'lg' }} color="#c9dbf2" mt="4" maxW="md">
          Maak spellen voor je teams, zet de storingen en incidenten van elke ronde klaar en verwerk
          de taken die de spelers kiezen.
        </Text>
        <Flex as="ul" direction="column" gap="3" listStyleType="none" m="0" p="0" mt="8">
          <Punt icoon={<LayoutGrid size="16" />}>Spellen maken en verwijderen</Punt>
          <Punt icoon={<Dices size="16" />}>Rondes voorbereiden en verwerken</Punt>
          <Punt icoon={<UserPlus size="16" />}>Andere spelleiders uitnodigen</Punt>
        </Flex>
      </Box>
    </Flex>
  );
}

function Punt({ icoon, children }: { icoon: ReactNode; children: string }) {
  return (
    <Flex as="li" alignItems="center" gap="3" color="#e4eefa" textStyle="md">
      <Flex
        aria-hidden
        flexShrink={0}
        boxSize="8"
        borderRadius="md"
        bg="whiteAlpha.100"
        color="#9cc3f5"
        alignItems="center"
        justifyContent="center"
      >
        {icoon}
      </Flex>
      {children}
    </Flex>
  );
}

/** Boven het formulier: het logo, de titel van het scherm en een korte uitleg. */
export function Formulierkop({ titel, children }: { titel: string; children?: ReactNode }) {
  return (
    <Flex alignItems="center" gap="4">
      <Image src="/icons/ame-logo.png" alt="" boxSize="14" flexShrink={0} borderRadius="md" />
      <Box minW="0">
        <Heading as="h1" textStyle="2xl" fontWeight="bold" color={NACHTBLAUW}>
          {titel}
        </Heading>
        {children && (
          <Text textStyle="sm" color="gray.600" overflowWrap="anywhere">
            {children}
          </Text>
        )}
      </Box>
    </Flex>
  );
}

/** Onderaan het formulier, achter een lijn: de weg terug of naar elders. */
export function Voetregel({ children }: { children: ReactNode }) {
  return (
    <Box borderTopWidth="1px" borderColor="gray.200" pt="4" textStyle="sm" color="gray.600">
      {children}
    </Box>
  );
}

/** Een link binnen de app, in het blauw van de knoppen. */
export function Naar({
  to,
  state,
  children,
}: {
  to: string;
  state?: unknown;
  children: ReactNode;
}) {
  return (
    <Link asChild color="ameBlue.600" fontWeight="semibold">
      <RouterLink to={to} state={state}>
        {children}
      </RouterLink>
    </Link>
  );
}

/** Een fout in het formulier, met de losse bezwaren van de server eronder. */
export function Formulierfout({ melding, fouten = [] }: { melding: string; fouten?: string[] }) {
  return (
    <Alert.Root status="error" role="alert">
      <Alert.Indicator />
      <Alert.Content>
        <Alert.Title>{melding}</Alert.Title>
        {fouten.length > 0 && (
          <Alert.Description asChild>
            <Box as="ul" ps="4" mt="1" listStyleType="disc">
              {fouten.map((fout) => (
                <li key={fout}>{fout}</li>
              ))}
            </Box>
          </Alert.Description>
        )}
      </Alert.Content>
    </Alert.Root>
  );
}

/** Een groene melding na het versturen. */
export function Gelukt({ children }: { children: ReactNode }) {
  return (
    <Alert.Root status="success" role="status">
      <Alert.Indicator />
      <Alert.Title fontWeight="normal">{children}</Alert.Title>
    </Alert.Root>
  );
}

/** Zolang de link uit de mail nog wordt opgezocht. */
export function Formulierskelet() {
  return (
    <Flex direction="column" gap="5" role="status" aria-busy="true" aria-label="Laden">
      <Flex alignItems="center" gap="4">
        <Skeleton boxSize="14" borderRadius="md" />
        <Box flex="1">
          <Skeleton h="6" w="60%" mb="2" />
          <Skeleton h="4" w="80%" />
        </Box>
      </Flex>
      <Skeleton h="11" />
      <Skeleton h="11" />
      <Skeleton h="11" />
    </Flex>
  );
}
